import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircleQuestion, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/** Current key — once set, the “find what you’re looking for” prompt never runs again in this browser profile. */
const DISMISS_STORAGE_KEY = 'zm_scroll_prompt_dismissed_v1';
/** Legacy key from earlier builds — still respected. */
const LEGACY_DISMISS_KEY = 'zm_site_ever';
/** Same-session guard (e.g. if localStorage is blocked). */
const SESSION_DISMISS_KEY = 'zm_scroll_prompt_session_done';

const MIN_MS_ON_PAGE = 28_000;
const MIN_SCROLL_PX = 380;

function isDismissedPersisted(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    if (window.sessionStorage.getItem(SESSION_DISMISS_KEY) === '1') return true;
    if (window.localStorage.getItem(DISMISS_STORAGE_KEY) === '1') return true;
    if (window.localStorage.getItem(LEGACY_DISMISS_KEY) === '1') return true;
  } catch {
    /* ignore */
  }
  return false;
}

function persistDismissed(): void {
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, '1');
    window.sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    // Migrate away from ambiguous legacy key so one source of truth remains
    if (window.localStorage.getItem(LEGACY_DISMISS_KEY) === '1') {
      window.localStorage.removeItem(LEGACY_DISMISS_KEY);
    }
  } catch {
    try {
      window.sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  }
}

/**
 * For first-time visitors (this browser), after meaningful scroll + time on page,
 * offers help finding products or contacting support. After dismiss, it does not show again.
 */
export default function ScrollDiscoveryPrompt() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = React.useState(false);
  const dismissed = React.useRef(false);
  const shownRef = React.useRef(false);

  React.useEffect(() => {
    if (isAuthenticated) return;
    if (typeof window === 'undefined') return;
    if (isDismissedPersisted()) return;

    let maxY = 0;
    const started = Date.now();

    const tryOpen = () => {
      if (isDismissedPersisted() || dismissed.current || shownRef.current) return;
      const elapsed = Date.now() - started;
      if (elapsed < MIN_MS_ON_PAGE) return;
      if (maxY < MIN_SCROLL_PX) return;
      shownRef.current = true;
      setOpen(true);
    };

    const onScroll = () => {
      maxY = Math.max(maxY, window.scrollY || 0);
      tryOpen();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    const id = window.setInterval(tryOpen, 3000);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.clearInterval(id);
    };
  }, [isAuthenticated]);

  const onClose = () => {
    // Persist first so any concurrent interval/scroll handler sees “dismissed” immediately.
    persistDismissed();
    dismissed.current = true;
    shownRef.current = true;
    setOpen(false);
  };

  if (isAuthenticated) return null;
  if (!open) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[55] flex justify-center p-4 sm:bottom-6 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="scroll-discovery-title"
    >
      <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl sm:p-5">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-lg p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          aria-label={t('common.close', 'Close')}
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex gap-3 pr-8">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
            <MessageCircleQuestion className="h-5 w-5" strokeWidth={2} />
          </div>
          <div>
            <h2 id="scroll-discovery-title" className="text-base font-semibold text-slate-900">
              {t('storefront.scroll_prompt_title', "Can't find what you're looking for?")}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {t(
                'storefront.scroll_prompt_body',
                'Browse the catalog, use search, or reach out — we are happy to help.',
              )}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/products"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
              >
                {t('storefront.scroll_prompt_catalog', 'View catalog')}
              </Link>
              <Link
                to="/feedback"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
              >
                {t('storefront.scroll_prompt_feedback', 'Send feedback')}
              </Link>
              <Link
                to="/auth"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
              >
                {t('storefront.scroll_prompt_account', 'Sign in')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
